import React, { useCallback, useState } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { SearchableDropdownField } from 'ui-components/components/dropdown';

import Global from '../../../../../../../../store/global-selectors';
import { PARAMETER } from '../../../../../constants/parameter-name-for-update';
import InputValueProps from '../../../../../prop-types/input-value-props';
import sharedStyles from '../../../../../style.module.less';
import messages from './messages';
import style from './style.module.less';
import UnexpectedValueComponent from './unexpected-value';

const LEGACY_RULE_VALUES = 3;
const NEW_RULE_VALUES = 4;

const getProvider = (providers, sellerFulfillmentProviderId, providerId) => {
  return (
    providers.find((prov) => prov.sellerFulfillmentProviderId === sellerFulfillmentProviderId) ||
    providers.find((prov) => prov.fulfillmentProviderId === providerId)
  );
};

const getFulfillmentProviderOptionLabel = (option) => option.name;
const getFulfillmentProviderOptionValue = (option) => option.sellerFulfillmentProviderId || option.fulfillmentProviderId;

const SetFulfillmentProvider = ({ inputValueProps, onChange }) => {
  const { parameter } = inputValueProps;
  const valueParts = parameter?.toString().split(',') || [];
  const [providerId, setProviderId] = useState(valueParts[0]);
  const [serviceId, setServiceId] = useState(valueParts[1]);
  const [automaticallySendRequestChange, setAutomaticallySendRequestChange] = useState(valueParts[2] === 'true');
  const [sellerFillProviderId, setSellerFillProviderId] = useState(valueParts[3]);
  const providers = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const services = useSelector(Global.selectSellerFulfillmentServices);
  const isNewAction = parameter === undefined;
  const valueDoesNotConformToStandard = !isNewAction && valueParts.length !== LEGACY_RULE_VALUES && valueParts.length !== NEW_RULE_VALUES;
  const intl = useIntl();

  const onSetProviderId = useCallback(
    (fulfillmentProviderId, sellerFulfillmentProviderId) => {
      setServiceId(undefined);
      setProviderId(fulfillmentProviderId);
      setSellerFillProviderId(sellerFulfillmentProviderId);
      onChange({
        [PARAMETER]: `${fulfillmentProviderId},${serviceId},${automaticallySendRequestChange},${sellerFulfillmentProviderId}`,
      });
    },
    [onChange, serviceId, automaticallySendRequestChange],
  );
  const onSetServiceId = useCallback(
    (servId) => {
      setServiceId(servId);
      onChange({
        [PARAMETER]: `${providerId},${servId},${automaticallySendRequestChange},${sellerFillProviderId}`,
      });
    },
    [onChange, providerId, automaticallySendRequestChange, sellerFillProviderId],
  );
  const onAutomaticallySendRequestChange = useCallback(
    (isSet) => {
      setAutomaticallySendRequestChange(isSet);
      onChange({
        [PARAMETER]: `${providerId},${serviceId},${isSet},${sellerFillProviderId}`,
      });
    },
    [onChange, providerId, serviceId, sellerFillProviderId],
  );

  const selectedProvider = getProvider(providers, sellerFillProviderId, providerId);
  const selectedService = services.find((serv) => serv.fulfillmentServiceId === serviceId);

  const updateSelectedProvider = useCallback(
    (_, newSelectedFulfillmentProvider) => {
      onSetProviderId(newSelectedFulfillmentProvider.fulfillmentProviderId, newSelectedFulfillmentProvider.sellerFulfillmentProviderId);
    },
    [onSetProviderId],
  );

  if (valueDoesNotConformToStandard) {
    return <UnexpectedValueComponent />;
  }

  return (
    <div className={style['multi-control']}>
      <div className={style['multi-control-row']}>
        <div>
          <FormattedMessage {...messages.shipFromLabel} />
        </div>
        <SearchableDropdownField
          getOptionLabel={getFulfillmentProviderOptionLabel}
          getOptionValue={getFulfillmentProviderOptionValue}
          onItemSelected={updateSelectedProvider}
          options={providers}
          placeholder={intl.formatMessage(messages.noOptionSelectedMessage)}
          selectedItemValue={selectedProvider?.sellerFulfillmentProviderId || selectedProvider?.fulfillmentProviderId}
        />
      </div>
      <div className={classnames(style.stacked, style['multi-control-row'])}>
        <div>
          <FormattedMessage {...messages.serviceLabel} />
        </div>
        <div>
          <DropdownMenu className={sharedStyles['dropdown-menu']} disabled={!selectedProvider} dropdownMenuShouldHaveWidthOfButton>
            <DropdownMenuToggler className={sharedStyles['dropdown-toggler']}>
              {selectedService && selectedService.name}
              {!selectedService && <FormattedMessage {...messages.noOptionSelectedMessage} />}
            </DropdownMenuToggler>
            {services
              .filter((serv) => serv.fulfillmentProviderId === selectedProvider?.fulfillmentProviderId)
              .map((service) => {
                return (
                  <DropdownMenuItem
                    key={service.fulfillmentServiceId}
                    className={sharedStyles['dropdown-item']}
                    onClick={() => onSetServiceId(service.fulfillmentServiceId)} // TODO: remove anon when dropdown supports typing for a further nested element
                    theme='unstyled'
                  >
                    {service.name}
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenu>
        </div>
      </div>
      <div className={style['multi-control-row']}>
        <div className={style['checkbox-wrap']}>
          <Checkbox checked={automaticallySendRequestChange} onChange={onAutomaticallySendRequestChange}>
            <span className={style.checkbox}>
              <FormattedMessage {...messages.autoSendLabel} />
            </span>
          </Checkbox>
        </div>
      </div>
    </div>
  );
};

SetFulfillmentProvider.propTypes = {
  inputValueProps: InputValueProps.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SetFulfillmentProvider;
